import { Block } from "@components/Block/Block";
import { FootnotesSection } from "@components/FootnotesSection/FootnotesSection";
import { JMCTableOfContents } from "@components/JMCTableOfContents/JMCTableOfContents";
import { MedicalExpertCard } from "@components/MedicalExpertCard/MedicalExpertCard";
import { SectionImpression } from "@components/SectionImpression/SectionImpression";
import { SMPCList } from "@components/SMPCList/SMPCList";
import { globalHistory } from "@reach/router";
import { CMSBlock, CMSFootnotes, CMSPersonProps, CMSProductProps, CMSSMPCProps, CombinedCvent } from "@types";
import classNames from "classnames";
import { graphql, useStaticQuery } from "gatsby";
import React from "react";

import style from "./style.module.scss";

export type PropTypes = {
    section: CMSBlock[];
    footnotes: CMSFootnotes[];
    placement: string;
    locale: string;
    showToc?: boolean;
    smpcs: CMSSMPCProps[];
    relatedProduct?: CMSProductProps;
    hasPaywall?: boolean;
    marginXl?: boolean;
    medicalExpert?: CMSPersonProps;
    cvents?: CombinedCvent[];
    addContentContainerWidth?: boolean;
    noTopPaddingFirstSection?: boolean;
};

/**
 * Body is a composite component that renders an array of CMSBlocks as block components underneath each other.
 */
export const Body = ({
    section,
    footnotes,
    placement,
    locale,
    showToc = false,
    relatedProduct,
    smpcs,
    cvents = [],
    marginXl = false,
    medicalExpert,
    addContentContainerWidth = false,
    noTopPaddingFirstSection = false,
}: PropTypes): JSX.Element => {
    const configurations: {
        nodes: {
            publish_details: { locale: string };
            seo_settings: {
                medexp_text: string;
            };
        }[];
    } = useStaticQuery(graphql`
        query MedexpCardTextQuery {
            shellConfigurations: allContentstackShell {
                nodes {
                    publish_details {
                        locale
                    }
                    seo_settings {
                        medexp_text
                    }
                }
            }
        }
    `);
    const configuration = configurations?.shellConfigurations?.nodes?.filter(
        (node) => node.publish_details.locale === locale,
    );

    const getUrlHash = (): string => {
        const hash = globalHistory?.location?.hash?.substr(1);
        return decodeURIComponent(hash);
    };

    return (
        <div data-test-id="eventDispatch">
            {showToc && <JMCTableOfContents content={section} />}
            {!section ||
                section.map((block: CMSBlock, index: number) => {
                    // 2 consecutive sections with the same background color should not have the double spacing in between
                    const noBottomSpacing =
                        !!section[index + 1] &&
                        section[index + 1]?.section?.background_color === block?.section?.background_color;
                    return (
                        <div key={index} className={block.section ? null : style.element}>
                            <div
                                className={classNames(
                                    style.block,
                                    addContentContainerWidth ? style.contentContainerWidth : null,
                                )}
                            >
                                <SectionImpression name={block?.section?.section_title} position={index + 1}>
                                    <Block
                                        relatedProduct={relatedProduct}
                                        content={block}
                                        footnotes={footnotes}
                                        noBottomSpacing={noBottomSpacing}
                                        index={index}
                                        sectionIndex={index}
                                        placement={placement}
                                        locale={locale}
                                        position={index + 1}
                                        cvents={cvents}
                                        hash={getUrlHash()}
                                        noTopPaddingFirstSection={noTopPaddingFirstSection}
                                    />
                                </SectionImpression>
                            </div>
                        </div>
                    );
                })}
            <div className={style.footerSection}>
                {footnotes && footnotes.length > 0 && <FootnotesSection footnotes={footnotes} />}
                <SMPCList smpcs={smpcs} products={relatedProduct ? [relatedProduct] : undefined} marginXl={marginXl} />
                {medicalExpert && (
                    <MedicalExpertCard
                        medicalExpert={medicalExpert}
                        medicalExpertText={configuration?.[0]?.seo_settings?.medexp_text}
                        pageTitle={placement}
                    />
                )}
            </div>
        </div>
    );
};
